import { BarChart3, TrendingUp, Zap, Calendar, CheckCircle2, ArrowLeft, Award } from "lucide-react";
import { useMemo } from "react";
import Card from "../components/ui/Card";
import { PRAYERS } from "../lib/constants";
import { useAppState } from "../context/AppStateContext";

function formatStreak(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / (24 * 3600));
  const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export default function AnalyticsPage({ goTo }) {
  const { state } = useAppState();
  
  // Debug logging for S25 Ultra issue
  if (process.env.NODE_ENV === 'development' || true) {
    console.log('AnalyticsPage loaded, state:', {
      hasHistory: !!state.history,
      historyLength: state.history?.length,
      hasPrayerLog: !!state.prayerLog,
      prayerLogKeys: Object.keys(state.prayerLog || {}).length,
    });
  }

  // Safe navigation function
  const handleGoTo = (tab) => {
    if (goTo && typeof goTo === 'function') {
      goTo(tab);
    }
  };

  // Calculate statistics - FIXED: Date preprocessing to prevent spam
  const stats = useMemo(() => {
    const history = state.history || [];
    
    if (history.length === 0) {
      return {
        totalSlips: 0,
        bestStreak: 0,
        averageStreak: 0,
        longestStreakMs: 0,
        shortestStreakMs: 0,
        slipReasons: {},
        slipsByDayOfWeek: {},
        slipsByTimeOfDay: {},
        last30DaysSlips: 0,
        recentTrend: "No data yet",
      };
    }

    // ✅ FIXED: Pre-process dates ONCE instead of recreating for each calculation
    const now = Date.now();
    const thirtyDaysAgoMs = now - (30 * 24 * 60 * 60 * 1000);
    
    const historyWithDates = history.map(h => ({
      ...h,
      timestamp: new Date(h.endAt).getTime(),
      date: new Date(h.endAt),
    }));

    const totalSlips = history.length;
    const durations = history.map((h) => h.durationMs);
    const longestStreakMs = Math.max(...durations);
    const shortestStreakMs = Math.min(...durations);
    const averageStreakMs = durations.reduce((a, b) => a + b, 0) / durations.length;

    const slipReasons = {};
    history.forEach((entry) => {
      if (entry.slipReasons && entry.slipReasons.length > 0) {
        entry.slipReasons.forEach((reason) => {
          slipReasons[reason] = (slipReasons[reason] || 0) + 1;
        });
      }
    });

    // ✅ FIXED: Use pre-processed dates
    const slipsByDayOfWeek = { Sun: 0, Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0 };
    const slipsByTimeOfDay = { Morning: 0, Afternoon: 0, Evening: 0, Night: 0 };
    let last30DaysSlips = 0;

    historyWithDates.forEach((entry) => {
      // Day of week
      const dayName = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][entry.date.getDay()];
      slipsByDayOfWeek[dayName]++;

      // Time of day
      const hour = entry.date.getHours();
      if (hour >= 5 && hour < 12) slipsByTimeOfDay.Morning++;
      else if (hour >= 12 && hour < 17) slipsByTimeOfDay.Afternoon++;
      else if (hour >= 17 && hour < 21) slipsByTimeOfDay.Evening++;
      else slipsByTimeOfDay.Night++;

      // Last 30 days
      if (entry.timestamp > thirtyDaysAgoMs) {
        last30DaysSlips++;
      }
    });

    const recentTrend =
      last30DaysSlips === 0
        ? "Great! No slips in the last 30 days"
        : last30DaysSlips <= 2
        ? "Excellent progress! Very few slips recently"
        : last30DaysSlips <= 5
        ? "Good progress, but stay vigilant"
        : "You're struggling lately. Consider reaching out for support";

    return {
      totalSlips,
      bestStreak: longestStreakMs,
      averageStreak: averageStreakMs,
      longestStreakMs,
      shortestStreakMs,
      slipReasons,
      slipsByDayOfWeek,
      slipsByTimeOfDay,
      last30DaysSlips,
      recentTrend,
    };
  }, [state.history]);

  // Prayer statistics
  const prayerStats = useMemo(() => {
    const prayerLog = state.prayerLog || {};
    const prayerCounts = {};
    let totalDays = 0;

    PRAYERS.forEach((prayer) => {
      prayerCounts[prayer] = 0;
    });

    Object.keys(prayerLog).forEach((iso) => {
      totalDays++;
      const dayPrayers = prayerLog[iso];
      PRAYERS.forEach((prayer) => {
        if (dayPrayers[prayer]) {
          prayerCounts[prayer]++;
        }
      });
    });

    const prayerPercentages = {};
    PRAYERS.forEach((prayer) => {
      prayerPercentages[prayer] = totalDays > 0 ? Math.round((prayerCounts[prayer] / totalDays) * 100) : 0;
    });

    // Only calculate best/worst if there's data
    let bestPrayer = null;
    let worstPrayer = null;

    if (totalDays > 0) {
      const percentageValues = Object.values(prayerPercentages);
      const maxPercentage = Math.max(...percentageValues);
      const minPercentage = Math.min(...percentageValues);

      // Only set bestPrayer if max is greater than 0 AND not all are the same (all 100%)
      if (maxPercentage > 0 && maxPercentage !== minPercentage) {
        bestPrayer = Object.keys(prayerPercentages).find((p) => prayerPercentages[p] === maxPercentage);
      }

      // Only set worstPrayer if min is less than 100 (not all perfect)
      if (minPercentage < 100) {
        worstPrayer = Object.keys(prayerPercentages).find((p) => prayerPercentages[p] === minPercentage);
      }
    }

    const consistencyMessage = totalDays === 0
      ? "Start tracking your prayers to see patterns"
      : Object.values(prayerPercentages).every(p => p === 0)
      ? "All prayers need attention. Start tracking to build consistency"
      : Object.values(prayerPercentages).every(p => p === 100)
      ? "MashaAllah! You're praying all 5 prayers consistently!"
      : prayerPercentages[bestPrayer] >= 80
      ? `Great! You're consistent with ${bestPrayer}`
      : prayerPercentages[bestPrayer] >= 50
      ? `You're doing okay with ${bestPrayer}, but can improve`
      : `All prayers need more attention`;

    return {
      prayerCounts,
      prayerPercentages,
      bestPrayer,
      worstPrayer,
      totalDays,
      consistencyMessage,
    };
  }, [state.prayerLog]);

  // Get top 5 reasons
  const topReasons = useMemo(() => {
    return Object.entries(stats.slipReasons)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [stats.slipReasons]);

  // Deed analytics - FIXED: Date optimization
  const deedStats = useMemo(() => {
    const deedLog = state.dailyDeeds?.log || {};
    const totalDeeds = Object.keys(deedLog).length;

    // ✅ FIXED: Pre-process dates ONCE
    const now = Date.now();
    const thirtyDaysAgoMs = now - (30 * 24 * 60 * 60 * 1000);
    
    const deedEntries = Object.values(deedLog);
    const deedTimestamps = deedEntries.map(d => new Date(d.completedAt).getTime());
    
    const last30DaysDeeds = deedTimestamps.filter(ts => ts > thirtyDaysAgoMs).length;

    // Break down by type
    const deedsByType = {};
    deedEntries.forEach((deed) => {
      const type = deed.type || "other";
      deedsByType[type] = (deedsByType[type] || 0) + 1;
    });

    // Calculate consistency: deeds per week average
    const oldestDeedMs = deedTimestamps.length > 0 ? Math.min(...deedTimestamps) : now;
    const daysTracked = Math.ceil((now - oldestDeedMs) / (1000 * 60 * 60 * 24)) || 1;
    const weeksTracked = Math.ceil(daysTracked / 7) || 1;
    const deediPerWeek = Math.round((totalDeeds / weeksTracked) * 10) / 10;

    return {
      totalDeeds,
      last30DaysDeeds,
      deediPerWeek,
      daysTracked,
      deedsByType,
    };
  }, [state.dailyDeeds]);
  // Get max frequency for chart scaling
  const maxReasonFreq = topReasons.length > 0 ? topReasons[0][1] : 1;
  const maxDayFreq = Math.max(...Object.values(stats.slipsByDayOfWeek)) || 1;
  const maxTimeFreq = Math.max(...Object.values(stats.slipsByTimeOfDay)) || 1;

  try {
    return (
      <div className="w-full space-y-4 flex flex-col" style={{ paddingBottom: "calc(80px + env(safe-area-inset-bottom, 20px))" }}>
        {/* Header with Back Button */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-lg font-semibold text-zinc-50">Detailed Insights</h1>
          <button
            onClick={() => handleGoTo("track")}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition text-sm font-medium"
          >
          <ArrowLeft size={16} />
          Back
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-zinc-900/50 border border-blue-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Zap size={18} className="text-blue-400" />
            <div className="text-xs font-semibold text-blue-300">Best Streak</div>
          </div>
          <div className="text-2xl font-bold text-blue-100">
            {formatStreak(stats.longestStreakMs)}
          </div>
        </Card>

        <Card className="bg-zinc-900/50 border border-purple-500/30">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={18} className="text-purple-400" />
            <div className="text-xs font-semibold text-purple-300">Average Streak</div>
          </div>
          <div className="text-2xl font-bold text-purple-100">
            {formatStreak(stats.averageStreak)}
          </div>
        </Card>

        <Card className="bg-zinc-900/50 border border-red-500/30">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 size={18} className="text-red-400" />
            <div className="text-xs font-semibold text-red-300">Total Slips</div>
          </div>
          <div className="text-2xl font-bold text-red-100">{stats.totalSlips}</div>
        </Card>

        <Card className="bg-zinc-900/50 border border-emerald-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Award size={18} className="text-emerald-400" />
            <div className="text-xs font-semibold text-emerald-300">Deeds Completed</div>
          </div>
          <div className="text-2xl font-bold text-emerald-100">{deedStats.totalDeeds}</div>
        </Card>

        <Card className="bg-zinc-900/50 border border-green-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Calendar size={18} className="text-green-400" />
            <div className="text-xs font-semibold text-green-300">Last 30 Days</div>
          </div>
          <div className="text-2xl font-bold text-green-100">{stats.last30DaysSlips}</div>
        </Card>
      </div>

      {/* Insight */}
      <Card className="bg-zinc-900/50 border-l-4 border-zinc-500">
        <div className="text-sm text-zinc-300">{stats.recentTrend}</div>
      </Card>

      {/* Prayer Consistency */}
      <Card title="Prayer Consistency" icon={CheckCircle2}>
        <div className="mb-4 p-3 bg-amber-900/20 rounded-lg">
          <div className="text-sm font-medium text-amber-900 dark:text-amber-100">{prayerStats.consistencyMessage}</div>
        </div>

        <div className="space-y-3">
          {PRAYERS.map((prayer) => {
            const percentage = prayerStats.prayerPercentages[prayer];
            const isGood = percentage >= 70;
            const isBest = prayerStats.bestPrayer && prayer === prayerStats.bestPrayer;
            const isWorst = prayerStats.worstPrayer && prayer === prayerStats.worstPrayer && percentage < 100;
            const needsWork = percentage === 0 && !isBest;

            return (
              <div key={prayer}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{prayer}</span>
                    {isBest && <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">Best</span>}
                    {isWorst && !needsWork && <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded">Needs work</span>}
                    {needsWork && <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded">Needs work</span>}
                  </div>
                  <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">{percentage}%</span>
                </div>
                <div className="w-full bg-zinc-200 dark:bg-zinc-800 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      isGood
                        ? "bg-gradient-to-r from-green-500 to-emerald-500"
                        : "bg-gradient-to-r from-orange-500 to-red-500"
                    }`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
          {prayerStats.totalDays === 0 
            ? "No prayer data yet. Start tracking!"
            : Object.values(prayerStats.prayerPercentages).every(p => p === 0)
            ? "All prayers are at 0%. Begin your journey today!"
            : `Based on ${prayerStats.totalDays} days of tracking. Focus on improving ${prayerStats.worstPrayer || "your weaker prayers"}.`
          }
        </div>
      </Card>

      {/* Top Slip Reasons */}
      {topReasons.length > 0 && (
        <Card title="Top Slip Reasons" icon={BarChart3}>
          <div className="space-y-3">
            {topReasons.map(([reason, count]) => (
              <div key={reason}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{reason}</span>
                  <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">{count}</span>
                </div>
                <div className="w-full bg-zinc-200 dark:bg-zinc-800 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-red-500 to-orange-500 h-full transition-all"
                    style={{ width: `${(count / maxReasonFreq) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
            Understanding your triggers helps you prepare better responses.
          </div>
        </Card>
      )}

      {/* Slips by Day of Week */}
      <Card title="Slips by Day of Week" icon={Calendar}>
        <div className="space-y-3">
          {Object.entries(stats.slipsByDayOfWeek).map(([day, count]) => (
            <div key={day}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{day}</span>
                <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">{count}</span>
              </div>
              <div className="w-full bg-zinc-200 dark:bg-zinc-800 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 h-full transition-all"
                  style={{ width: `${(count / maxDayFreq) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
          {(() => {
            const entries = Object.entries(stats.slipsByDayOfWeek);
            if (entries.length === 0) return "No data";
            return entries.reduce((a, b) => (b[1] > a[1] ? b : a))[0];
          })()}{" "}
          is your most challenging day.
        </div>
      </Card>

      {/* Slips by Time of Day */}
      <Card title="Slips by Time of Day" icon={TrendingUp}>
        <div className="space-y-3">
          {Object.entries(stats.slipsByTimeOfDay).map(([time, count]) => (
            <div key={time}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{time}</span>
                <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">{count}</span>
              </div>
              <div className="w-full bg-zinc-200 dark:bg-zinc-800 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-amber-500 to-orange-500 h-full transition-all"
                  style={{ width: `${(count / maxTimeFreq) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
          {(() => {
            const entries = Object.entries(stats.slipsByTimeOfDay);
            if (entries.length === 0) return "No data";
            return entries.reduce((a, b) => (b[1] > a[1] ? b : a))[0];
          })()}{" "}
          is your most vulnerable time.
        </div>
      </Card>

      {/* Deeds Analytics */}
      {deedStats.totalDeeds > 0 && (
        <Card title="Deeds Progress" icon={Award}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-emerald-950/20 border border-emerald-900/30">
                <div className="text-xs text-zinc-500 mb-1">Total Deeds</div>
                <div className="text-2xl font-bold text-[#10B981]">{deedStats.totalDeeds}</div>
              </div>
              <div className="p-3 rounded-lg bg-emerald-950/20 border border-emerald-900/30">
                <div className="text-xs text-zinc-500 mb-1">Last 30 Days</div>
                <div className="text-2xl font-bold text-[#10B981]">{deedStats.last30DaysDeeds}</div>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-emerald-950/20 border border-emerald-900/30">
              <div className="text-xs text-zinc-500 mb-1">Average Per Week</div>
              <div className="text-2xl font-bold text-[#10B981]">{deedStats.deediPerWeek}</div>
            </div>

            {/* Breakdown by Type */}
            {Object.keys(deedStats.deedsByType).length > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <div className="text-xs text-zinc-500 mb-3 font-semibold">Deed Types</div>
                <div className="space-y-2">
                  {Object.entries(deedStats.deedsByType).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between">
                      <div className="text-sm capitalize text-zinc-200">
                        {type === 'audio' && '🎵 Audio Listening'}
                        {type === 'prayer' && '🤲 Prayers'}
                        {type === 'reflection' && '📝 Reflections'}
                        {type === 'asmaul_husna' && '📿 99 Names of Allah'}
                        {type === 'other' && '✨ Other'}
                      </div>
                      <div className="text-sm font-bold text-[#10B981]">{count}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-sm text-zinc-400">
              {deedStats.totalDeeds > 10
                ? "MashaAllah! You're on fire with your deeds!"
                : deedStats.totalDeeds > 5
                ? "Great progress! Keep building momentum"
                : "Every deed counts. Keep going!"}
            </div>
          </div>
        </Card>
      )}

      {/* No Data State */}
      {stats.totalSlips === 0 && prayerStats.totalDays === 0 && deedStats.totalDeeds === 0 && (
        <Card className="text-center py-6">
          <div className="text-sm text-zinc-600 dark:text-zinc-400">
            No data yet. Start tracking to see insights!
          </div>
        </Card>
      )}
    </div>
    );
  } catch (error) {
    console.error('AnalyticsPage Error:', error);
    return (
      <div className="w-full space-y-4 p-4">
        <div className="text-red-400 text-center">
          <div className="font-semibold mb-2">Error Loading Insights</div>
          <div className="text-sm text-zinc-400 mb-4">{error?.message || 'Unknown error'}</div>
          <button
            onClick={() => handleGoTo("track")}
            className="px-4 py-2 bg-zinc-800 text-white rounded-lg"
          >
            Back to Calendar
          </button>
        </div>
      </div>
    );
  }
}

